import { useCallback, useEffect, useState } from 'react';

import { COLUMN_WIDTHS_STORAGE_KEY } from './constants';
import { clampColumnWidth, readStoredColumnWidths, writeStoredColumnWidths } from './utils';

export type SetColumnWidth = (code: string, nextWidth: number, persist?: boolean) => void;

const COLUMN_WIDTHS_CHANGED_EVENT = 'spot-history-table-column-widths-changed';

function readColumnWidths() {
  return readStoredColumnWidths(COLUMN_WIDTHS_STORAGE_KEY);
}

function notifyColumnWidthsChanged(widths: Record<string, number>) {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(new CustomEvent(COLUMN_WIDTHS_CHANGED_EVENT, { detail: widths }));
}

export function useColumnWidths() {
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() =>
    readColumnWidths()
  );

  useEffect(() => {
    const syncColumnWidths = (event?: Event) => {
      const nextWidths =
        event instanceof CustomEvent && event.detail && typeof event.detail === 'object'
          ? (event.detail as Record<string, number>)
          : readColumnWidths();

      setColumnWidths(nextWidths);
    };

    const syncStoredColumnWidths = (event: StorageEvent) => {
      if (event.key !== COLUMN_WIDTHS_STORAGE_KEY) return;

      syncColumnWidths();
    };

    window.addEventListener(COLUMN_WIDTHS_CHANGED_EVENT, syncColumnWidths);
    window.addEventListener('storage', syncStoredColumnWidths);
    return () => {
      window.removeEventListener(COLUMN_WIDTHS_CHANGED_EVENT, syncColumnWidths);
      window.removeEventListener('storage', syncStoredColumnWidths);
    };
  }, []);

  const setColumnWidth = useCallback<SetColumnWidth>(
    (code, nextWidth, persist = true) => {
      const next = { ...readColumnWidths(), ...columnWidths, [code]: clampColumnWidth(nextWidth) };
      setColumnWidths(next);
      if (!persist) return;

      writeStoredColumnWidths(COLUMN_WIDTHS_STORAGE_KEY, next);
      notifyColumnWidthsChanged(next);
    },
    [columnWidths]
  );

  return { columnWidths, setColumnWidth };
}
